/**
 * branchPremise.ts — is a branch's PREMISE (its fork node) confirmed, contradicted,
 * or still open?
 *
 * A branch asserts something: "assume event X occurs" / "assume it does not". Once
 * the world has had its say, that assertion is either borne out or it isn't. This
 * module is the pure decision layer for that comparison — no React, no store, no
 * fetch. Callers supply a verdict and a clock; this returns a state.
 *
 * TWO DIFFERENT RESOLUTIONS live on one lane and must never be conflated:
 *
 *   • the QUESTION resolving — at `contract.resolution_date`, verdict on
 *     `run.resolution`, rendered by `resolvedOutcome()`. Subject: the forecast.
 *   • the PREMISE resolving — at the FORK's own window, subject: the trigger.
 *     That is this module.
 *
 * The premise question is always binary ("did X occur by D?") even when the parent
 * forecast is multi-valued, so nothing here is outcome-space aware. That is the
 * whole reason premise grading works on the resolution rail that exists today.
 *
 * ── The asymmetric window ────────────────────────────────────────────────────
 * Occurrence is MONOTONE: once an event has happened it has happened permanently,
 * but "it didn't happen" is only knowable once its window shuts. So decisiveness
 * is asymmetric, and that asymmetry is the heart of this module:
 *
 *   a YES verdict  → decisive anywhere in the window (it happened; done)
 *   a NO verdict   → decisive only AFTER the window's end
 *
 * Assert "did not occur" mid-window and you are calling a race before the horses
 * are in. See `isVerdictDecisive`.
 */

import { parseResolveDateLocal, resolutionMomentLocal } from "../formatDates.js";
import { sameSourceEvent, sameSourceTerms, type SourceEventIdentity } from "./derivedBranch.js";
import type { ForecastEvent } from "./model.js";
import contractStatus from "../../../../shared/contractStatus.json";

/** What the user asserted when they made the branch. Mirrors the `sense` that
 *  `branchTrigger.js::triggerToAssumption` computes and sends to the forecast
 *  server, which is why the spellings must match it exactly. */
export type PremiseSense = "occurs" | "not_occurs" | "reschedule" | "custom";

/** Where a branch's premise stands. Terminal states are write-once. */
export type PremiseState = "pending" | "confirmed" | "contradicted" | "indeterminate";

/** A verdict reduced to what the premise comparison cares about. `null` means the
 *  resolver has said nothing usable yet (no attempt, or still in flight). */
export type PremiseOutcome = "yes" | "no" | "indeterminate";

/** DEBUG-ONLY forced premise state. Lets every branch UI state be previewed without
 *  minting contracts and waiting on real resolver verdicts (which need a past window and
 *  take minutes). Purely a view-layer override: it is threaded into `premiseFor` and never
 *  written anywhere, so it cannot persist or reach the data plane. */
export type PremiseOverride =
  | "aligned"
  | "misaligned"
  | "pending"
  | "watching"
  | "overdue"
  | "indeterminate";

/** Why an attempt is being considered. The frequency policy is event-driven:
 *  every re-forecast, plus once at the window's open and once at its close. */
export type PremiseAttemptReason = "reforecast" | "window_open" | "window_close";

/** The fork-event fields this module reads. Structurally typed — the stored fork
 *  event is untyped jsonb and carries plenty besides these. */
export interface ForkEventLike {
  id?: string | null;
  date?: string | null;
  date_end?: string | null;
  p_event?: number | null;
  probability?: number | null;
  _deleted?: boolean;
  /** The CLICKED parent event's id, stamped by `branchTrigger.js`. The join back
   *  to the un-collapsed date range — see `premiseWindow`. */
  _sourceEventId?: string | null;
  /** Canonical identity frozen with `_sourceEventId`; both must match before a
   *  current parent event can supply this fork's range. */
  _sourceEventIdentity?: SourceEventIdentity | null;
  /** Contract-grade trio, present on a trigger that has its own contract (every
   *  machine trigger in contract mode, and a TYPED trigger once preprocessed). */
  question?: string | null;
  resolution_criteria?: string | null;
  resolution_time?: string | null;
  /** The key of the contract the forecast server wrote for this trigger (`PremiseKeyLike`). */
  contract_key?: string | null;
}

/** The persisted premise record (lives in the branch row's `fork` jsonb). */
export interface PremiseRecord {
  state?: PremiseState;
  verdict?: string | null;
  /** What settled it: the premise contract's verdict, or its window closing with none.
   *  "minted" is the same as "verdict", from before premises were keys. */
  source?: "verdict" | "closed" | "minted" | "inline" | "none";
  gradedAt?: string | null;
  /** Dedup only, NOT a throttle — it stops the window-open and window-close
   *  attempts from re-firing across reloads. Re-forecast attempts are never
   *  throttled. */
  lastAttemptAt?: string | null;
  resolvableKey?: string | null;
  /** When the user acknowledged the settled premise and cleared the lane. Set means
   *  "hide the branch", NOT "delete it": the row is kept so the conditional forecast
   *  stays scoreable and so rewinding to an earlier vintage can still show it. */
  clearedAt?: string | null;
}

/** The `fork` jsonb as this module reads it. */
export interface ForkLike {
  forkDate?: string | null;
  modification?: string | null;
  forkEvent?: ForkEventLike | null;
  sense?: string | null;
  premise?: PremiseRecord | null;
}

/** A premise window in epoch ms. `endMs` is the END of the end-date (next local
 *  midnight), so a trigger whose window closes today is still open today. */
export interface PremiseWindow {
  startMs: number;
  endMs: number;
}

export const SENSES: readonly string[] = ["occurs", "not_occurs", "reschedule", "custom"];

const TERMINAL_STATES: readonly string[] = contractStatus.premise;

/** True for a state that must never be recomputed once written. */
export function isTerminalPremiseState(state: string | null | undefined): boolean {
  return !!state && (TERMINAL_STATES as readonly string[]).includes(state);
}

/**
 * Derive the asserted sense from a fork EVENT alone.
 *
 * Deliberately mirrors `commands/reforecast.ts::branchAssumption`, which makes the
 * same occur-vs-not call from the same fields — two spellings of one decision would
 * be two chances to disagree about what a branch asserted.
 *
 * Returns null when the fork event does not express a clear assertion (a mid-range
 * probability nudge, or no event at all). `reschedule` is NOT derivable here: a
 * date-move fork leaves no marker on the event, which is exactly why `fork.sense`
 * is persisted at create time — see `premiseSenseOf`.
 */
export function deriveSenseFromForkEvent(
  fe: ForkEventLike | null | undefined,
): PremiseSense | null {
  if (!fe) return null;
  // Custom user-typed events are minted with a `ce-` id by handleCustomEventBranch.
  if (typeof fe.id === "string" && fe.id.startsWith("ce-")) return "custom";
  // A removed event is an assertion that it does NOT happen. `branchTrigger.js`
  // maps modType 'delete' → sense 'not_occurs'; the probability is left at the
  // original event's value, so it must be checked BEFORE the numeric rules below
  // (which would otherwise read a mid-range original as "no assertion").
  if (fe._deleted) return "not_occurs";
  const p = fe.p_event != null ? fe.p_event : fe.probability;
  if (p == null) return null;
  if (p >= 0.9) return "occurs";
  if (p <= 0.1) return "not_occurs";
  return null;
}

/**
 * The sense a branch asserted: the persisted `fork.sense` when present, else
 * derived from the fork event.
 *
 * Persisted wins because it is the value the branch was actually CREATED with
 * (`branchTrigger.js` computed it and the forecast server was told it), and because
 * `reschedule` cannot be recovered from the event afterwards. Derivation is the
 * legacy path, for branch rows that predate persistence.
 */
export function premiseSenseOf(fork: ForkLike | null | undefined): PremiseSense | null {
  if (!fork) return null;
  if (fork.sense && SENSES.includes(fork.sense)) return fork.sense as PremiseSense;
  return deriveSenseFromForkEvent(fork.forkEvent);
}

/**
 * What a branch's fork node says before the trigger title, by asserted sense. Branching
 * one trigger both ways draws two fork nodes with the SAME title, so without this the only
 * difference between the "occurs" and "does not occur" lanes is the node's outline. Same
 * wording as the report's conditional pair ("If it happens" / "If not"). Reschedule and
 * custom forks carry their own wording in the title, so they get no prefix.
 */
const SENSE_LABEL_PREFIX: Readonly<Record<string, string>> = {
  occurs: "If it happens",
  not_occurs: "If not",
};

/** The fork node's label: the trigger title prefixed with the branch's assumption. */
export function forkNodeLabel(title: string, sense: string | null | undefined): string {
  const prefix = sense ? SENSE_LABEL_PREFIX[sense] : undefined;
  if (!prefix) return title;
  return title ? `${prefix}: ${title}` : prefix;
}

/**
 * The window inside which the premise could come true, in epoch ms.
 *
 * A canonically matching `parentEvents` entry is preferred but no longer load-bearing.
 * `branchTrigger.js` used to
 * COLLAPSE an event-mod fork event's date range (`date_end: null`, `date` = the range
 * MIDPOINT), so the real window survived only on the PARENT's copy, reachable through the
 * `_sourceEventId` that the same builder stamps. Fork events now carry their range
 * directly, so the fallback is usually equivalent — but ROWS FORKED BEFORE THAT CHANGE
 * still have a collapsed fork event. Those legacy rows lack canonical identity, so they
 * now fail closed to their frozen range instead of risking an unrelated event that reused
 * the same result-local id.
 *
 * The FALLBACK to `fe` is what covers a custom-event fork: those carry no `_sourceEventId`,
 * so the lookup never matches and the fork's own dates are the only window there is. It also
 * covers a fork whose parent event has since left the list.
 *
 * Returns null when there is no parseable start date.
 */
export function premiseWindow(
  fe: ForkEventLike | null | undefined,
  parentEvents?: readonly ForkEventLike[] | null,
): PremiseWindow | null {
  if (!fe) return null;
  const source =
    fe._sourceEventId && Array.isArray(parentEvents)
      ? parentEvents.find((e) =>
          sameSourceEvent(e as ForecastEvent, fe as Partial<ForecastEvent>),
        ) || null
      : null;
  // The source event's range wins whole — taking its `date` but the fork's
  // `date_end` (or vice versa) would mix two different events' geometry.
  const from = source || fe;
  const start = parseResolveDateLocal(from.date ?? null);
  if (!start || isNaN(start.getTime())) return null;
  // End-of-the-end-date, as the next local midnight: a single-day trigger dated
  // today is open through today, not shut at this morning's midnight. No question
  // text is passed — resolutionMomentLocal's "before" rule is about how a FORECAST
  // question is phrased and has no bearing on a trigger's window.
  const endStr = from.date_end || from.date;
  const end = resolutionMomentLocal(endStr ?? null);
  const endMs = end && !isNaN(end.getTime()) ? end.getTime() : null;
  return {
    startMs: start.getTime(),
    // A malformed date_end must not produce a window that ends before it starts —
    // fall back to the start date's own end-of-day.
    endMs: endMs != null && endMs >= start.getTime() ? endMs : start.getTime(),
  };
}

/**
 * Reduce a resolver verdict status to the premise comparison's vocabulary.
 *
 * `resolved_tie` and `unresolved` both mean "the resolver ran and could not call
 * it" — distinct from null, which means it has not usefully run at all. Anything
 * else (in_flight, error, absent) is null: no information, not a negative result.
 */
export function verdictOutcomeOf(status: string | null | undefined): PremiseOutcome | null {
  if (status === "resolved_yes") return "yes";
  if (status === "resolved_no") return "no";
  // A void premise contract (cancelled, or a conditional one whose own condition failed)
  // will never say yes or no, and why it was voided does not reach chatboard.
  if (
    status === "resolved_tie" ||
    status === "unresolved" ||
    status === "voided" ||
    status === "void"
  )
    return "indeterminate";
  return null;
}

/**
 * May this verdict be acted on yet? The asymmetric-window rule.
 *
 * A YES is decisive immediately: occurrence is monotone, so an event that has
 * happened cannot un-happen before its window shuts, and waiting would only delay
 * a state we already know. A NO (or an indeterminate) is decisive only once the
 * window has ENDED — mid-window it means "not yet", not "never".
 *
 * `nowMs > endMs` is strict, matching the end-of-day `endMs` from `premiseWindow`:
 * a window ending today is still open at every instant of today.
 */
export function isVerdictDecisive(args: {
  outcome: PremiseOutcome | null;
  window: PremiseWindow | null;
  nowMs: number;
}): boolean {
  const { outcome, window, nowMs } = args;
  if (!outcome) return false;
  if (outcome === "yes") return true;
  if (!window) return false;
  return nowMs > window.endMs;
}

/**
 * The grace the forecast server's trigger-contract gate allows past a contract's close
 * (sooth-agents `trigger_contracts.CLOSE_GRACE_MS`). A premise is only called never-settled
 * once even a late write could no longer land, so the two can never disagree about a branch
 * whose contract is being written right now.
 */
export const MINT_CLOSE_GRACE_MS = 86400000;

/**
 * Has the premise's window closed with nothing that could ever settle it?
 *
 * A verdict requires a minted trigger contract, and two ordinary shapes can never have one:
 * a fork carrying no contract at all (nothing to mint — so the window shutting decides it
 * outright, with no grace to wait out), and a fork whose contract was never minted before
 * its close, after which the mint gate refuses.
 *
 * `mintExists` is the CALLER's evidence, and null means "cannot tell", which never
 * concludes — the two callers genuinely see different things: the server sweep queries
 * every minted row for the tree, the browser only knows the fork's stamps plus whatever
 * runs the store happens to hold. Absence of proof is not proof of absence, and the state
 * this produces is a user-facing claim.
 *
 * That claim is deliberately NOT "the trigger did not occur" — nobody checked. It says the
 * branch is no longer live, and nothing about the world.
 */
export function premiseNeverSettles(args: {
  window: PremiseWindow | null;
  gradable: boolean;
  mintExists: boolean | null;
  nowMs: number;
}): boolean {
  const { window, gradable, mintExists, nowMs } = args;
  if (!window) return false;
  if (nowMs <= window.endMs) return false; // still open — the same strict end as a verdict
  if (!gradable) return true; // no contract → no mint → no verdict, ever
  if (mintExists !== false) return false; // minted, or unknown: a verdict may still come
  // `>=`, matching the mint gate's own `closeMs + CLOSE_GRACE_MS <= nowMs`: at equality the
  // mint is already refused, so waiting one more instant would leave a premise pending that
  // nothing could ever settle. The two boundaries agree only where the runtime is UTC —
  // this one is an end-of-day LOCAL, the mint's is end-of-day UTC — so treat them as the
  // same rule, not the same number.
  return nowMs >= window.endMs + MINT_CLOSE_GRACE_MS;
}

/**
 * The premise state for a branch, given a verdict and a clock.
 *
 * The sense table (`occurs` + yes → confirmed, `occurs` + no → contradicted, and
 * the inverse for `not_occurs`) is applied ONLY to a decisive verdict; everything
 * else stays `pending`. `reschedule` is always pending: "move event X to <date>"
 * is not an occur/not-occur assertion, so a yes/no verdict does not test it, and
 * claiming otherwise would grade a branch against a question it never asked.
 */
export function premiseStateOf(args: {
  sense: PremiseSense | null;
  verdictStatus: string | null | undefined;
  window: PremiseWindow | null;
  nowMs: number;
  /** Does the fork carry the contract trio the mint gate requires? */
  gradable?: boolean;
  /** Has a trigger contract actually been minted? `null`/absent = cannot tell. */
  mintExists?: boolean | null;
}): PremiseState {
  const { sense, verdictStatus, window, nowMs } = args;
  if (!sense || sense === "reschedule") return "pending";
  const outcome = verdictOutcomeOf(verdictStatus);
  if (!isVerdictDecisive({ outcome, window, nowMs })) {
    // No decisive verdict — but a window that shut with nothing able to settle it is
    // terminal too, and calling it `pending` promised a verdict that could never arrive.
    // Checked only on the no-verdict path, so a real verdict always wins.
    return premiseNeverSettles({
      window,
      gradable: args.gradable !== false,
      mintExists: args.mintExists ?? null,
      nowMs,
    })
      ? "indeterminate"
      : "pending";
  }
  if (outcome === "indeterminate") return "indeterminate";
  const occurred = outcome === "yes";
  // `custom` asserts occurrence, like `occurs` — a user typing an event in is
  // saying "suppose this happens".
  const assertedOccurrence = sense === "occurs" || sense === "custom";
  return occurred === assertedOccurrence ? "confirmed" : "contradicted";
}

/**
 * Is the premise inside its window but not yet decided? The fourth fork-node UI
 * state — a branch sitting live past its own trigger date reads as a bug without
 * it, so it is named here rather than inferred at the render site.
 *
 * The window's OPEN is compared day-granularly, its close is not. That asymmetry is
 * deliberate and matches the clock it is handed: App.jsx quantizes `nowMs` to the start
 * of the local day (a stable memo key, and premise verdicts are day decisions), so a
 * window that opens at a real time-of-day — which a hand-placed trigger's does, since it
 * now carries the instant the user clicked — would not read as open until the FOLLOWING
 * midnight. The node would sit un-watched through the whole day it was placed on. Only
 * the close keeps sub-day strictness, where erring late is the safe direction (see
 * `isVerdictDecisive`).
 */
export function isPremiseWatching(args: {
  state: PremiseState;
  window: PremiseWindow | null;
  nowMs: number;
}): boolean {
  const { state, window, nowMs } = args;
  if (state !== "pending" || !window) return false;
  const open = new Date(window.startMs);
  if (isNaN(open.getTime())) return false;
  open.setHours(0, 0, 0, 0);
  return nowMs >= open.getTime();
}

/**
 * Has the premise's window closed with no ruling yet? A verdict can still arrive (the
 * resolver rules late), but the trigger's own dates no longer explain the wait. Strict at the
 * close, the same end as `isVerdictDecisive`. A reschedule (or a premise of unknown sense)
 * never gets a ruling to wait for, so it is never overdue.
 */
export function isPremiseOverdue(args: {
  state: PremiseState;
  sense: PremiseSense | null;
  window: PremiseWindow | null;
  nowMs: number;
}): boolean {
  const { state, sense, window, nowMs } = args;
  if (state !== "pending" || !window || !sense || sense === "reschedule") return false;
  return nowMs > window.endMs;
}

/**
 * Should a grading attempt fire now, for this reason?
 *
 * The frequency policy is EVENT-DRIVEN, not throttled: every re-forecast (manual
 * or scheduled) attempts, plus one attempt at the window's open and one at its
 * close. Nothing in between.
 *
 * `lastAttemptAt` is dedup, not a rate limit — it keeps the once-only window_open
 * and window_close attempts from re-firing on every reload. A `reforecast` attempt
 * is never suppressed by it: the user asked for a fresh look, and the whole point
 * of re-forecasting is that the world may have moved.
 *
 * Nothing fires before the window opens (there is nothing to observe yet) or once
 * the state is terminal (write-once).
 */
export function premiseAttemptWarranted(args: {
  reason: PremiseAttemptReason;
  state: PremiseState;
  window: PremiseWindow | null;
  nowMs: number;
  lastAttemptAt?: string | null;
}): boolean {
  const { reason, state, window, nowMs, lastAttemptAt } = args;
  if (isTerminalPremiseState(state)) return false;
  if (!window) return false;
  if (nowMs < window.startMs) return false;
  if (reason === "reforecast") return true;
  const lastMs = lastAttemptAt ? new Date(lastAttemptAt).getTime() : NaN;
  const attempted = !isNaN(lastMs);
  if (reason === "window_open") {
    // Once, at or after the open. Any prior attempt already covers it — including
    // a re-forecast that happened to land inside the window first.
    return !attempted;
  }
  // window_close: once, and only once the window has actually ended.
  if (nowMs <= window.endMs) return false;
  return !attempted || lastMs <= window.endMs;
}

// ── Finding the verdict ──────────────────────────────────────────────────────
// A branch's premise is a contract the forecast server wrote: a generated premise's key rides
// the fork event (`contract_key`, copied from the parent's event, which the parent's run
// stamped), and a typed premise's is stamped there once the branch's own run writes it. The
// SERVER grades the branch when the premise's verdict arrives (branchPremiseSync) and writes
// the result to `fork.premise`, so that record is the browser's only source for the verdict.

/** The premise key a fork event carries. */
export interface PremiseKeyLike {
  contract_key?: string | null;
}

/** The run fields the premise views read off the store (the parent's events, the trunk's
 *  re-forecast clock): typed loosely, as the store declares them. */
export interface PremiseCandidateRun {
  forecastId?: string | null;
}

/** An untyped premise blob narrowed to the record shape, or null. */
function premiseRecordOf(premise: unknown): PremiseRecord | null {
  if (!premise || typeof premise !== "object" || Array.isArray(premise)) return null;
  return premise as PremiseRecord;
}

/** The premise contract's key, or null when the fork names none (a reschedule, a typed
 *  premise its run has not written yet, or a premise from before keys). */
export function premiseKeyOf(
  fe: (ForkEventLike & PremiseKeyLike) | null | undefined,
): string | null {
  const key = fe?.contract_key;
  return typeof key === "string" && key ? key : null;
}

/**
 * Translate a debug override into a forced state + a matching synthetic verdict.
 *
 * "Aligned" means the world agreed with the user, so the verdict has to AGREE with the
 * asserted sense; "misaligned" is its opposite. Deriving the verdict rather than hardcoding
 * one keeps the node's ✓/✕ consistent with the panel — otherwise a forced-confirmed branch
 * could show a ✕, which is exactly the contradiction the two-channel encoding exists to
 * avoid.
 */
function overrideView(
  override: PremiseOverride | null | undefined,
  sense: PremiseSense | null,
): {
  state: PremiseState;
  verdictStatus: string | null;
  watching: boolean;
  overdue: boolean;
} | null {
  if (!override) return null;
  if (override === "pending" || override === "watching" || override === "overdue") {
    return {
      state: "pending",
      verdictStatus: null,
      watching: override !== "pending",
      overdue: override === "overdue",
    };
  }
  if (override === "indeterminate") {
    return { state: "indeterminate", verdictStatus: "unresolved", watching: false, overdue: false };
  }
  // `custom` asserts occurrence like `occurs`; `reschedule` has no occur/not-occur claim, so
  // it is treated as occurs purely to give the debug view a definite glyph.
  const assertsOccur = sense !== "not_occurs";
  const agreed = override === "aligned";
  const occurred = agreed ? assertsOccur : !assertsOccur;
  return {
    state: agreed ? "confirmed" : "contradicted",
    verdictStatus: occurred ? "resolved_yes" : "resolved_no",
    watching: false,
    overdue: false,
  };
}

/**
 * Can this premise be graded AT ALL — is there a contract behind it?
 *
 * The three fields the forecast server's trigger-contract gate requires
 * (sooth-agents `trigger_contracts.is_qualifying`), so this answers "will a verdict ever
 * arrive for this fork" with the same test that decides whether one was ever asked for.
 *
 * It exists to keep the UI from promising a verdict that cannot come. A typed trigger whose
 * preprocess failed carries no contract, so its premise sits pending forever — and labelling
 * that "pending resolution" states something false about the future rather than merely
 * showing nothing.
 */
export function premiseGradable(fe: ForkEventLike | null | undefined): boolean {
  if (!fe) return false;
  return !!(
    (fe.question || "").trim() &&
    (fe.resolution_criteria || "").trim() &&
    (fe.resolution_time || "").trim()
  );
}

/** Everything a caller needs about one branch's premise, in one call. */
export interface PremiseView {
  sense: PremiseSense | null;
  window: PremiseWindow | null;
  state: PremiseState;
  /** In-window but undecided — the fourth fork-node UI state. */
  watching: boolean;
  /** Window closed, still undecided — see `isPremiseOverdue`. Implies `watching`. */
  overdue: boolean;
  /** Has the window closed at `nowMs`, whatever the state? The same strict end as `overdue`. */
  windowClosed: boolean;
  /** Is a verdict actually coming? See `premiseGradable` — a fork with no contract behind
   *  it must not be shown as awaiting one. */
  gradable: boolean;
  /** Is a TERMINAL premise actually WRITTEN on the row, as opposed to computed here? The
   *  server's clear predicate tests the STORED state, so a surface offering CLEAR has to ask
   *  this rather than `state`, or it enables a button the server answers 409 to. */
  settled: boolean;
  /** Has the premise settled on the REAL data, whether or not it is written yet? Distinct
   *  from `settled` (stored) and from `state`, which a debug override can force — a forced
   *  preview is view-only, so it must never make CLEAR actionable. */
  computedSettled: boolean;
  verdictStatus: string | null;
  source: "verdict" | "none";
  resolvableKey: string | null;
  /** Has the TRUNK re-forecast since this premise was graded? Gates the terminal panel's
   *  "is now incorporated" claim — see `premiseIncorporated`. */
  incorporated: boolean;
}

/**
 * Resolve a branch's premise from the live store: sense, window, verdict, state.
 *
 * A PERSISTED terminal state wins over anything recomputed here. Premise states
 * are write-once by design, and recomputing one would let a later verdict revision
 * (or a clock change — the debug today-picker moves `nowMs`) silently flip a branch
 * the user has already been shown as settled.
 *
 * `parentEvents` should be the PARENT run's events — see `premiseWindow` for why
 * omitting them collapses every ranged trigger to a single day.
 */
export function premiseFor(args: {
  fork: ForkLike | null | undefined;
  parentEvents?: readonly ForkEventLike[] | null;
  nowMs: number;
  /** The TRUNK's last re-forecast moment, for the incorporated gate. */
  trunkLastReforecastedAt?: string | null;
  /** DEBUG-ONLY forced state — see `PremiseOverride`. */
  override?: PremiseOverride | null;
}): PremiseView {
  const { fork, parentEvents, nowMs } = args;
  const fe = (fork?.forkEvent ?? null) as (ForkEventLike & PremiseKeyLike) | null;
  const sense = premiseSenseOf(fork);
  const window = premiseWindow(fe, parentEvents);
  // The verdict is the one the server wrote on `fork.premise`, with its grade, or with the
  // pending state while it cannot decide yet (a NO or void before the window closes); the
  // browser never sees the premise contract's verdict otherwise. The key names that contract.
  const record = premiseRecordOf(fork?.premise);
  const resolvableKey = premiseKeyOf(fe) || record?.resolvableKey || null;
  const verdictStatus = typeof record?.verdict === "string" ? record.verdict : null;
  const persisted = fork?.premise?.state ?? null;
  // DEBUG override wins over BOTH the persisted state and the derived one — the point is to
  // preview a state the data does not currently produce. It also synthesizes the verdict, so
  // the fork node's ✓/✕ (which reads reality, not the assertion) matches the forced state
  // instead of contradicting it.
  const overridden = overrideView(args.override, sense);
  const state = overridden
    ? overridden.state
    : isTerminalPremiseState(persisted)
      ? (persisted as PremiseState)
      : premiseStateOf({
          sense,
          verdictStatus,
          window,
          nowMs,
          gradable: premiseGradable(fe),
          // The browser's evidence, deliberately conservative: a key proves a written premise
          // contract, and nothing here rules one out (null, never false) — the server sweep
          // is the one that settles those.
          mintExists: resolvableKey ? true : null,
        });
  return {
    sense,
    window,
    state,
    watching: overridden ? overridden.watching : isPremiseWatching({ state, window, nowMs }),
    overdue: overridden ? overridden.overdue : isPremiseOverdue({ state, sense, window, nowMs }),
    windowClosed: !!window && nowMs > window.endMs,
    // A debug override is previewing a state the data does not produce, so it implies a
    // gradable premise — otherwise forcing "watching" would render nothing.
    gradable: args.override ? true : premiseGradable(fe),
    settled: isTerminalPremiseState(persisted),
    computedSettled: !overridden && isTerminalPremiseState(state),
    verdictStatus: overridden ? overridden.verdictStatus : verdictStatus,
    source: verdictStatus ? "verdict" : "none",
    resolvableKey,
    incorporated: premiseIncorporated({
      premise: premiseRecordOf(fork?.premise),
      trunkLastReforecastedAt: args.trunkLastReforecastedAt,
    }),
  };
}

/** A branch run as the aggregator below reads it. */
export interface PremiseBranchRun {
  runId?: string | null;
  parentRunId?: string | null;
  forkEvent?: ForkEventLike | null;
  forkSense?: string | null;
  premise?: unknown;
  /** When CLEAR BRANCH archived it (ISO); an archived scenario is not rerun or asked about. */
  clearedAt?: string | null;
}

/**
 * Premise views for a whole lane's branches, keyed by runId.
 *
 * The parent lookup is what makes ranged triggers correct: each branch's window
 * comes from ITS OWN parent's events (the root for a direct fork, another branch
 * for a sub-fork), because that is where the un-collapsed date range still lives.
 */
export function premiseViewsFor(args: {
  branchRuns: readonly PremiseBranchRun[] | null | undefined;
  runsById:
    | Record<string, PremiseCandidateRun & { events?: unknown; lastReforecastedAt?: unknown }>
    | null
    | undefined;
  nowMs: number;
  /** The TRUNK run's id, so each branch can be told whether the trunk has caught up. */
  rootRunId?: string | null;
  /** DEBUG-ONLY forced state, applied to every branch — see `PremiseOverride`. */
  override?: PremiseOverride | null;
}): Record<string, PremiseView> {
  const { branchRuns, runsById, nowMs } = args;
  const out: Record<string, PremiseView> = {};
  if (!Array.isArray(branchRuns) || !branchRuns.length) return out;
  const byId = runsById || {};
  // Read off the TRUNK, not the branch: "incorporated in the mainline forecast" is a claim
  // about the trunk's number, so a branch's own re-forecast says nothing about it.
  const rootRun = args.rootRunId ? byId[args.rootRunId] : null;
  const trunkAt =
    typeof rootRun?.lastReforecastedAt === "string" ? rootRun.lastReforecastedAt : null;
  for (const br of branchRuns) {
    if (!br || !br.runId) continue;
    const parent = br.parentRunId ? byId[br.parentRunId] : null;
    const parentEvents = Array.isArray(parent?.events) ? (parent.events as ForkEventLike[]) : null;
    out[br.runId] = premiseFor({
      fork: {
        forkEvent: br.forkEvent ?? null,
        sense: br.forkSense ?? null,
        premise: premiseRecordOf(br.premise),
      },
      parentEvents,
      nowMs,
      trunkLastReforecastedAt: trunkAt,
      override: args.override,
    });
  }
  return out;
}

/** How a parent timeline marks a past trigger that its scenarios have not settled. */
export type ScenarioOutcome = "watching" | "overdue" | "undetermined";

/**
 * Mark each past trigger whose outcome its scenarios have not settled, so the parent timeline
 * draws "?" instead of a ✓ it has no grounds for (an unmarked past trigger reads as happened).
 *
 * A scenario finds its trigger by the terms it froze (`sameSourceTerms`), not by id, since a
 * rerun may renumber the parent's events. The terms are read from `originals`, the row's stored
 * events matched by id, because the lane clips displayed dates to the forecast's resolution and
 * a clipped end date would change them.
 *
 * `children` includes scenarios the user cleared: clearing one says nothing about the world,
 * so it must not turn the trigger's "?" back into a ✓.
 *
 * A confirmed or contradicted scenario leaves the trigger alone (its ✓ or ✕ is the server's
 * mark). Otherwise a pending scenario wins over an undetermined one, since a ruling may still
 * come. The server's `occurred` mark outranks pending scenarios, but not an undetermined one,
 * even beside a pending sibling: a tie or void verdict is stored as "did not happen", while the
 * scenario says nobody could tell. A reschedule asserts nothing about occurrence, and a pending
 * premise with no contract has no ruling coming, so both are ignored.
 *
 * A trigger whose scenario's window has closed at the displayed moment (`windowClosed`) is past
 * whatever its stored `isPast` says.
 *
 * The status goes on a COPY as `_scenarioOutcome` (with `_recheckable` when a pending premise
 * that is not archived has a contract, which Update now asks about again), view fields like
 * `_neutral`; a past trigger
 * cannot start a scenario, so the copy never becomes a fork event. Returns `events` itself
 * when nothing is marked.
 */
export function withScenarioOutcomes<
  E extends ForkEventLike & { isPast?: boolean; occurred?: unknown },
>(
  events: readonly E[],
  children: readonly PremiseBranchRun[],
  views: Readonly<Record<string, PremiseView | null | undefined>> | null | undefined,
  originals: readonly ForkEventLike[] | null | undefined = events,
): readonly E[] {
  if (!children.length || !views) return events;
  const stored = new Map((originals || []).map((e) => [e?.id, e]));
  let changed = false;
  const out = events.map((ev) => {
    if (!ev) return ev;
    const source = ((ev.id != null && stored.get(ev.id)) || ev) as ForkEventLike as ForecastEvent;
    let pending: "overdue" | "watching" | null = null;
    let undetermined = false;
    let recheckable = false;
    let closed = false;
    for (const child of children) {
      const view = child.runId ? views[child.runId] : null;
      if (!view?.sense || view.sense === "reschedule") continue;
      if (!sameSourceTerms(source, child.forkEvent as Partial<ForecastEvent>)) continue;
      if (view.state === "confirmed" || view.state === "contradicted") return ev;
      if (view.windowClosed) closed = true;
      if (view.state === "pending") {
        if (!view.gradable) continue;
        pending = view.overdue || pending === "overdue" ? "overdue" : "watching";
        // Update now skips an archived scenario (selectUnarchivedBranchRuns).
        if (view.resolvableKey && !child.clearedAt) recheckable = true;
      } else undetermined = true;
    }
    const outcome: ScenarioOutcome | null =
      typeof ev.occurred === "boolean"
        ? undetermined
          ? "undetermined"
          : null
        : (pending ?? (undetermined ? "undetermined" : null));
    // A stored `isPast: false` lags a window that closed while the page was open
    // (refreshLanePastFlags only clears the flag), so a window closed by the premise clock (the
    // displayed moment, rewinds included) makes the trigger past too.
    if (!outcome || (!ev.isPast && !closed)) return ev;
    changed = true;
    const recheck = recheckable && outcome !== "undetermined";
    return {
      ...ev,
      isPast: true,
      _scenarioOutcome: outcome,
      ...(recheck && { _recheckable: true }),
    };
  });
  return changed ? out : events;
}

// ── Is the premise actually reflected in the trunk yet? ─────────────────────

/**
 * Has the TRUNK re-forecast since this premise was graded?
 *
 * This is the honesty gate on the terminal panel's copy. "…is now incorporated in the
 * mainline forecast" is a claim about the trunk's number, and it is only true once the
 * trunk has re-run with the event in the past. Until then the truthful wording is the
 * future tense.
 *
 * **The exposure is not a race, it is up to a full cadence interval.** Three clocks are
 * involved and only two of them are coupled: the trigger's window closes, the resolver
 * grades it on its own post-close ladder, and the trunk re-forecasts on its cadence. A
 * manual ↻ reruns trunk and branches together so it holds immediately there; on the
 * schedule they are independent contracts, so a weekly forecast can sit graded-but-not-
 * yet-incorporated for days.
 *
 * Compares against `gradedAt` rather than the trigger's window end because that is the
 * moment the verdict became knowable to us — a trunk run before it may pre-date the
 * resolver's own determination even if it post-dates the event.
 *
 * Absent either timestamp → false. Unknown is never asserted as incorporated.
 */
export function premiseIncorporated(args: {
  premise?: PremiseRecord | null;
  trunkLastReforecastedAt?: string | null;
}): boolean {
  const gradedAt = args.premise?.gradedAt;
  const trunkAt = args.trunkLastReforecastedAt;
  if (!gradedAt || !trunkAt) return false;
  const g = new Date(gradedAt).getTime();
  const t = new Date(trunkAt).getTime();
  if (isNaN(g) || isNaN(t)) return false;
  return t >= g;
}

/**
 * Is this branch SUPERSEDED — the ROOT's question resolved while the branch's own
 * premise never settled?
 *
 * The third branch-terminal state, and the only one that is a fact about the TRUNK
 * rather than about the trigger. A branch is a forecast of the ROOT's question under
 * an assumption; once the root's question carries a verdict there is nothing left for
 * that conditional to be a forecast OF, whatever became of the assumption. Without
 * this a branch under a resolved root kept rendering in lane colour with a live
 * probability marker, reading as an open forecast of a question already answered.
 *
 * A REAL premise verdict wins. `confirmed` / `contradicted` are statements about the
 * trigger the user actually asserted, and "the trunk resolved" must never overwrite
 * one — so only a premise that is neither (pending, watching, or `indeterminate`:
 * the grader ran and could not decide) is superseded.
 *
 * Keyed on the root's TERMINAL VERDICT, deliberately NOT on the lane's wider
 * `showVerdictVisual` (which also covers "past its resolution moment with no
 * verdict"). Rejected because an unresolved-but-past root is a different fact —
 * nobody has graded it — and treating it as superseding would retire live branches
 * on every root whose resolver merely has not run yet.
 *
 * This is a RENDER state derived at read time, and that is the point: per the design
 * doc it must NOT set `contract_status = 'resolved'` on the branch and must NOT write
 * `fork.premise`. Premise state is its own field, so a late genuine grading still
 * lands and still wins over this.
 */
export function branchSuperseded(args: {
  rootHasTerminalVerdict: boolean;
  premiseState?: PremiseState | string | null;
}): boolean {
  if (!args.rootHasTerminalVerdict) return false;
  const s = args.premiseState;
  return s !== "confirmed" && s !== "contradicted";
}

/** Has the user acknowledged and cleared this settled branch? */
export function premiseCleared(premise?: PremiseRecord | null): boolean {
  return !!premise?.clearedAt;
}

/**
 * The premise record to hold after reading `incoming` from the server, or null to keep `held`.
 * Reads can land out of order, so a terminal grade is never replaced by a non-terminal one, and
 * a CLEAR stamp already held survives a read taken before the clear. An unchanged record is null.
 */
export function nextPremiseRecord(held: unknown, incoming: unknown): PremiseRecord | null {
  const next = premiseRecordOf(incoming);
  if (!next) return null;
  const prior = premiseRecordOf(held);
  if (isTerminalPremiseState(prior?.state) && !isTerminalPremiseState(next.state)) return null;
  const merged =
    prior?.clearedAt && !next.clearedAt ? { ...next, clearedAt: prior.clearedAt } : next;
  return JSON.stringify(prior) === JSON.stringify(merged) ? null : merged;
}
